import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { marzoLines } from '@/mocks/marzo';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

export default function MarzoPage() {
  const [activeTab, setActiveTab] = useState('altis');

  useSEO({
    title: 'Marzo Vitorino Caruaru PE | Móveis Corporativos – Home Office Premium',
    description: 'Marzo Vitorino em Caruaru PE. Móveis corporativos de alta tecnologia: Altis, Attuale, Impéria Premium, Prima Arq, Romanzza Plus e muito mais. Revendedor autorizado.',
    keywords: 'Marzo Vitorino Caruaru, móveis corporativos Pernambuco, estações de trabalho, mesas escritório, Marzo Vitorino revendedor',
    path: '/marzo-vitorino',
    ogTitle: 'Marzo Vitorino Caruaru PE | Home Office Premium',
    ogDescription: 'Revendedor autorizado Marzo Vitorino em Caruaru PE. Alta tecnologia, beleza e bem-estar para ambientes corporativos.',
  });

  useSchemaLD({
    id: 'schema-marzo',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Marzo Vitorino Caruaru PE – Home Office Premium',
      description: 'Revendedor autorizado Marzo Vitorino em Caruaru PE. Móveis corporativos de alto padrão: mesas, estações de trabalho e mobiliário para escritórios modernos.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/marzo-vitorino`,
      about: {
        '@type': 'Brand',
        name: 'Marzo Vitorino',
        description: 'Referência nacional em mobiliário corporativo de alto padrão. 16 linhas exclusivas com certificação FSC.',
      },
    },
  });

  useSchemaLD({
    id: 'schema-marzo-breadcrumb',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com' },
        { '@type': 'ListItem', position: 2, name: 'Marcas', item: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/marcas` },
        { '@type': 'ListItem', position: 3, name: 'Marzo Vitorino', item: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/marzo-vitorino` },
      ],
    },
  });

  useSchemaLD({
    id: 'schema-marzo-faq',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Onde comprar móveis Marzo Vitorino em Caruaru e Pernambuco?',
          acceptedAnswer: { '@type': 'Answer', text: 'A Home Office Premium é revendedora autorizada Marzo Vitorino em Caruaru PE, atendendo todo o estado de Pernambuco, incluindo Recife, Petrolina, Garanhuns e região. Temos toda a linha com entrega, montagem e suporte técnico.' },
        },
        {
          '@type': 'Question',
          name: 'Quais são as linhas de móveis Marzo Vitorino disponíveis?',
          acceptedAnswer: { '@type': 'Answer', text: 'A Marzo Vitorino possui mais de 16 linhas exclusivas, incluindo Altis, Attuale, Impéria Premium, Prima Arq, Romanzza Plus e outras. Cada linha é desenvolvida para diferentes perfis de escritório, do operacional ao executivo de alto padrão.' },
        },
        {
          '@type': 'Question',
          name: 'Os móveis Marzo Vitorino têm certificação ambiental?',
          acceptedAnswer: { '@type': 'Answer', text: 'Sim. A Marzo Vitorino possui certificação FSC®, que garante que a madeira utilizada na fabricação vem de florestas manejadas de forma sustentável e responsável.' },
        },
        {
          '@type': 'Question',
          name: 'Qual a garantia dos móveis Marzo Vitorino?',
          acceptedAnswer: { '@type': 'Answer', text: 'Os móveis Marzo Vitorino possuem garantia de fábrica de 5 anos contra defeitos de fabricação. A Home Office Premium oferece suporte técnico pós-venda em Caruaru e região.' },
        },
      ],
    },
  });

  const activeLine = marzoLines.find(l => l.id === activeTab);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 h-[520px] md:h-[600px] overflow-hidden">
        <img
          src="https://www.marzovitorino.com.br/imgs/attualehomemobileb502f3_231025080631.jpg"
          alt="Marzo Vitorino"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-transparent"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 bg-[#c0392b] text-white text-xs font-semibold px-4 py-2 rounded-full mb-6 uppercase tracking-widest">
                <i className="ri-star-fill text-xs"></i>
                Marca Parceira Oficial
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
                Marzo<br />
                <span className="text-[#c0392b]">Vitorino</span>
              </h1>
              <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8">
                Alta tecnologia, beleza e bem-estar para ambientes corporativos. 16 linhas exclusivas para escritórios modernos, refeitórios e espaços de trabalho.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://www.marzovitorino.com.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-gray-900 text-sm font-semibold px-6 py-3 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  Ver Catálogo Completo
                </a>
                <a
                  href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#c0392b] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#a93226] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i>
                  Solicitar Orçamento
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sobre */}
      <section className="py-20 bg-[#fafaf8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#c0392b] text-xs font-semibold uppercase tracking-widest mb-3">Quem é a Marzo Vitorino</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Referência nacional em mobiliário corporativo de alto padrão
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                A Marzo Vitorino é uma das marcas mais reconhecidas do Brasil no segmento de móveis para escritórios corporativos. Com sede em Mairiporã-SP, a empresa une design exclusivo, tecnologia avançada e sustentabilidade em cada produto.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                Somos revendedores autorizados da Marzo Vitorino em Caruaru e região, oferecendo toda a linha de produtos com entrega, montagem e suporte técnico especializado.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: 'ri-shield-check-line', label: 'Garantia de Fábrica' },
                  { icon: 'ri-truck-line', label: 'Entrega em todo Brasil' },
                  { icon: 'ri-tools-line', label: 'Suporte Técnico' },
                  { icon: 'ri-award-line', label: 'Revendedor Autorizado' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#c0392b]/10 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-[#c0392b] text-sm`}></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '16+', label: 'Linhas de Produtos' },
                { value: '300+', label: 'Modelos no Catálogo' },
                { value: 'FSC®', label: 'Certificação Ambiental' },
                { value: 'BR', label: 'Fabricação Nacional' },
              ].map(stat => (
                <div key={stat.label} className="bg-white rounded-2xl p-6 text-center border border-gray-100">
                  <p className="text-3xl font-bold text-[#c0392b] mb-1">{stat.value}</p>
                  <p className="text-gray-500 text-xs">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Linhas com fotos reais */}
      <section id="linhas" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#c0392b] text-xs font-semibold uppercase tracking-widest mb-3">Catálogo</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Linhas Marzo Vitorino</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">
              Selecione uma linha para ver detalhes. Clique em "Ver no site oficial" para explorar todos os modelos e configurações.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {marzoLines.map(line => (
              <button
                key={line.id}
                onClick={() => setActiveTab(line.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === line.id
                    ? 'bg-[#c0392b] text-white'
                    : 'bg-[#f5f5f0] text-gray-600 hover:bg-[#ece9e4]'
                }`}
              >
                <i className={`${line.icon} text-sm`}></i>
                {line.label}
              </button>
            ))}
          </div>

          {/* Foto grande + CTA + grid de fotos */}
          {activeLine && (
            <>
              {/* Foto principal grande + texto */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12">
                <div className="rounded-2xl overflow-hidden h-[480px] bg-[#f8f8f6]">
                  <img
                    src={activeLine.image}
                    alt={activeLine.label}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <p className="text-[#c0392b] text-xs font-semibold uppercase tracking-widest mb-3">Linha {activeLine.label}</p>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{activeLine.description}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-8">
                    Trabalhamos com toda a linha <strong>{activeLine.label}</strong> da Marzo Vitorino. Acesse o catálogo oficial para ver todos os modelos, acabamentos e especificações técnicas. Depois é só entrar em contato com a gente para orçamento.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={activeLine.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#c0392b] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#a93226] transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-external-link-line"></i>
                      Ver linha no site Marzo Vitorino
                    </a>
                    <a
                      href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#f5f5f0] text-gray-700 text-sm font-medium px-6 py-3 rounded-full hover:bg-[#ece9e4] transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line"></i>
                      Pedir orçamento
                    </a>
                  </div>
                </div>
              </div>

              {/* Grid de fotos adicionais — máx 4 */}
              {activeLine.products.length > 1 && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                  {activeLine.products.slice(0, 4).map(product => (
                    <a
                      key={product.name}
                      href={activeLine.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group bg-[#f8f8f6] rounded-2xl overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#c0392b]/30 transition-all duration-300"
                    >
                      <div className="h-64 w-full overflow-hidden flex items-center justify-center p-4">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="px-4 pb-4">
                        <p className="text-gray-700 text-sm font-semibold truncate">{activeLine.label}</p>
                        <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
                          Ver no site <i className="ri-external-link-line"></i>
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Como funciona */}
      <section className="py-16 bg-[#fafaf8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-8">
            <p className="text-gray-900 font-bold text-base mb-6 text-center">Como funciona?</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { step: '01', icon: 'ri-search-eye-line', title: 'Acesse o catálogo', desc: 'Navegue pelo site oficial da Marzo Vitorino e escolha a linha e os produtos que te interessam.' },
                { step: '02', icon: 'ri-whatsapp-line', title: 'Fale com a gente', desc: 'Mande o nome ou foto do produto pelo WhatsApp. Nossa equipe cuida do orçamento e da compra.' },
                { step: '03', icon: 'ri-truck-line', title: 'Receba em casa', desc: 'Cuidamos da compra, entrega e montagem. Você só aproveita o resultado final.' },
              ].map(item => (
                <div key={item.step} className="flex flex-col items-center text-center">
                  <div className="relative mb-4">
                    <div className="w-14 h-14 flex items-center justify-center bg-[#c0392b]/10 rounded-2xl">
                      <i className={`${item.icon} text-[#c0392b] text-2xl`}></i>
                    </div>
                    <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-[#c0392b] text-white text-xs font-bold rounded-full">{item.step}</span>
                  </div>
                  <h4 className="text-gray-900 font-bold text-sm mb-2">{item.title}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[#c0392b] text-xs font-semibold uppercase tracking-widest mb-4">Revendedor Autorizado</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Quer equipar seu escritório com<br className="hidden md:block" /> produtos Marzo Vitorino?
          </h2>
          <p className="text-gray-400 text-sm mb-10 max-w-lg mx-auto">
            Fale com nossa equipe e receba um orçamento personalizado. Atendemos empresas, escritórios, escolas e espaços corporativos em Caruaru e região.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#c0392b] text-white text-sm font-semibold px-8 py-4 rounded-full hover:bg-[#a93226] transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-whatsapp-line"></i>
              Falar no WhatsApp
            </a>
            <a href="https://www.marzovitorino.com.br" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/20">
              <i className="ri-external-link-line"></i>
              Ver Catálogo Oficial
            </a>
            <Link to="/contato"
              className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/20">
              <i className="ri-mail-line"></i>
              Enviar Mensagem
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
